import type { ReactNode } from 'react'
import { CartesianGrid, ComposedChart, Line, Tooltip } from 'recharts'
import {
  CROSSHAIR,
  PLOT_TOP,
  PREV_LINE,
  PlotContainer,
  PeriodTooltip,
  currentFirst,
  gridProps,
  xAxis,
  yAxis,
  yScale,
  type PeriodPoint,
} from './chartCardParts'

/**
 * Counts over time (Paper "Charts · Card pattern", line card): this period as
 * a 2px monotone line in `color`, the previous period as a 1.5px mist-400 line
 * dashed "4 4" on the same x positions. No fills, no dots at rest. Hover adds
 * a 1px mist-300 crosshair, a dot on each series (r4 / r3, white fill) and the
 * app's near-black tooltip.
 *
 * Pass `showPrevious={false}` (or no previous values) when the previous
 * window's buckets are not available: the dashed line is then not drawn.
 */
export function TrendLineChart({
  data,
  labels,
  color = 'var(--chart-1)',
  showPrevious = true,
  formatValue = (n) => n.toLocaleString('en-US'),
  formatAxis,
  overlay,
}: {
  data: PeriodPoint[]
  labels: { current: string; previous: string }
  color?: string
  showPrevious?: boolean
  formatValue?: (n: number) => string
  formatAxis: (n: number) => string
  /** Rendered over the plot (empty state) */
  overlay?: ReactNode
}) {
  const withPrevious = showPrevious && data.some((p) => p.previous != null)
  const { ticks, domain } = yScale(data, withPrevious)
  const config = {
    current: { label: labels.current, color },
    previous: { label: labels.previous, color: PREV_LINE },
  }

  return (
    <div className="relative">
      <PlotContainer config={config}>
        {/* Right margin keeps the last date label inside the card (points sit on the edge) */}
        <ComposedChart data={data} margin={{ top: PLOT_TOP, right: 18, bottom: 0, left: 0 }}>
          <CartesianGrid {...gridProps} />
          {xAxis(data.length)}
          {yAxis(ticks, domain, formatAxis)}
          {!overlay && (
            <Tooltip
              cursor={{ stroke: CROSSHAIR, strokeWidth: 1 }}
              offset={12}
              isAnimationActive={false}
              itemSorter={currentFirst}
              content={
                <PeriodTooltip
                  labels={labels}
                  colors={{ current: color, previous: PREV_LINE }}
                  format={formatValue}
                />
              }
            />
          )}
          {withPrevious && (
            <Line
              dataKey="previous"
              name="previous"
              type="monotone"
              stroke={PREV_LINE}
              strokeWidth={1.5}
              strokeDasharray="4 4"
              strokeLinecap="round"
              dot={false}
              connectNulls={false}
              activeDot={{ r: 3, fill: 'var(--background)', stroke: PREV_LINE, strokeWidth: 1.5 }}
              isAnimationActive={false}
            />
          )}
          {!overlay && (
            <Line
              dataKey="current"
              name="current"
              type="monotone"
              stroke={color}
              strokeWidth={2}
              strokeLinecap="round"
              dot={false}
              activeDot={{ r: 4, fill: 'var(--background)', stroke: color, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </PlotContainer>
      {overlay}
    </div>
  )
}

export default TrendLineChart
